import {flow, getEnv, types} from "mobx-state-tree";
import {GroupSelectState} from "../../elements/groups/GroupSelectState";
import DesignerState from "../designer/DesignerState";
import QrPreviewState from "../qrpreview/QrPreviewState";
import ImageConfigManager from "../../../managers/ImageConfigs/ImageConfigManager";
import PreviewManager from "../../../managers/preview/PreviewManager";
import QrConfiguration from "../../../models/QrConfiguration";
import {QrConfigSelectState} from "../../elements/qrconfig/QrConfigSelectState";
import Notification from "../../../utils/Notification";
import {setDefaultBackgroundColor} from "../../../utils/common";
import { PREVIEW_IMAGE_SIZE } from "../../../utils/constants"
import {generateImageCheckscannability} from "../../../utils/scannability";
import { DashboardStore } from "src/stores/domain/DashboardStore";
import {resolveIdentifier} from "mobx-state-tree";

export const DesignerModalState = types.model("DesignerModalState", {
  isLoading: types.optional(types.boolean, false),
  isUploadingMedia: types.optional(types.boolean, false),
  noDesignOpen:types.optional(types.boolean, false),
  customOpen:types.optional(types.boolean, true),
  groupSelectStore: types.maybe(types.reference(GroupSelectState)),
  isMadeDefault:types.optional(types.boolean, false),
  designerViewStore:types.maybe(types.late(() => DesignerState)),
  qrConfigSelectStore:types.maybe(types.reference(QrConfigSelectState)),
  qrPreviewStore:types.maybe(types.late(() => QrPreviewState)),
  isEditCodePage: types.optional(types.boolean, false),
  isView: types.optional(types.boolean, false),
  // dashboardStore: types.maybe(types.reference(DashboardStore)),
  isRemovedDesignerModal: types.optional(types.boolean, false),
  defaultImageConfig: types.optional(types.frozen(), {}),
  v1ImageConfig: types.optional(types.frozen(), {})
}).volatile((self) => ({
  updateImageCodeUuid: types.fn,
  updateImageSelectedInPreview: types.fn,
}))
  .actions(self => {
    return ({
      handleIsDefault() {
        self.isMadeDefault = !self.isMadeDefault
        console.log(self)
      },
      setNoneOpen() {
        self.noDesignOpen = true
        self.customOpen = false
      },
      setCustomOpen() {
        self.noDesignOpen = false
        self.customOpen = true
      },
      setConsumer(fn) {
        self.updateImageCodeUuid = fn
      },
      setConsumeUpdateImageSelectedInPreview(fn){
        self.updateImageSelectedInPreview = fn
      },
      consumeUtmSelect() {
        self.utmSelectState.setConsumer(self.fetchUtmWithID)
      },
      createQrPreview: flow(function* f(extendConfig) {
        const extend = JSON.parse(extendConfig)
        setDefaultBackgroundColor(extend)
        const formDataForIcon = JSON.stringify({...extend ,size: PREVIEW_IMAGE_SIZE.size, resolution: PREVIEW_IMAGE_SIZE.resolution, unitsOfMeasure: PREVIEW_IMAGE_SIZE.unit, showImageSizeOrModuleWidth: true})
        const configForIcon = {
          name:"",
          qrImageFormData:formDataForIcon,
          shortUrl:""
        }
        const designerHttpClient = getEnv(self).httpClient; // Use authenticated httpClient from environment
        const previewManager = new PreviewManager(designerHttpClient, self.defaultImageConfig, self.v1ImageConfig)
        const res = yield previewManager.create(configForIcon)
        self.isLoading = false
        return res
      }),
      applyDesign: flow(function* f(currentState, defaultConfig = null) {
        try {
          if(self.noDesignOpen) {
            if(self.designerViewStore.qrConfigSelectStore && self.designerViewStore.qrConfigSelectStore.configStore) {
              // self.designerViewStore.qrConfigSelectStore.selectConfig(self.designerViewStore.qrConfigSelectStore.configStore.defaultConfiguration[1])
              self.designerViewStore.qrConfigSelectStore.configStore.removeImageConfig()
              currentState?.handleImageUuidChange?.("")
              self.designerViewStore.resetExtendConfig()
              return "no design selected"
            }
          }
          let {extendConfig} = self?.designerViewStore

          // Ensure logo defaults are set before applying (same validation as Preview)
          const parsedConfig = JSON.parse(extendConfig)
          if (parsedConfig.logo && (!parsedConfig.logoPercentageWidth || !parsedConfig.logoMargin)) {
            if (!parsedConfig.logoPercentageWidth) {
              parsedConfig.logoPercentageWidth = 27
            }
            if (!parsedConfig.logoMargin) {
              parsedConfig.logoMargin = 1
            }
            extendConfig = JSON.stringify(parsedConfig)
            self.designerViewStore.extendConfig = extendConfig
          }

          const isScannable = yield generateImageCheckscannability(JSON.parse(extendConfig), self.defaultImageConfig, self.v1ImageConfig, getEnv(self).httpClient)

          // Only Batch operation, we need to create a config
          if(currentState?.batchType) {
            if(defaultConfig) {
              currentState?.handleImageUuidChange?.(defaultConfig.uuid)
            } else {
              const imageConfigManager = getEnv(self).imageConfigManager
              const newImageConfigUuid = yield imageConfigManager.addImageConfigForCode(self, extendConfig)
              currentState?.handleImageUuidChange?.(newImageConfigUuid)
            }
          }
        } catch (error) {
          console.error("Error in applyDesign:", error);
          new Notification()
            .setType("error")
            .setMessage("Error applying design")
            .send();
        }
      }),

      updateDesign: flow(function* f(uuid, currentState) {
        try {
          if(self.noDesignOpen) {
            self.designerViewStore.qrConfigSelectStore.configStore.removeImageConfig()
            self.updateImageCodeUuid("")
            self.designerViewStore.resetExtendConfig()
            return "no design selected"
          }
          if(currentState?.batchType) {
            const {extendConfig} = self?.designerViewStore
            const imageConfigManager = getEnv(self).imageConfigManager
            const newImageConfigUuid = yield imageConfigManager.addImageConfigForCode(self, extendConfig)
            self.updateImageCodeUuid(newImageConfigUuid)
          }
          return true
        } catch (error) {
          console.error("Error in updateDesign:", error);
          new Notification()
            .setType("error")
            .setMessage("Error updating design")
            .send();
          return false
        }
      }),
      setRemovedDesignerModal(bool) {
        self.isRemovedDesignerModal = bool
      },
      setIsUploadingMedia(isUploading) {
        self.isUploadingMedia = isUploading
      },
      setupMediaFileUploadListeners() {
        // Set up upload status consumers for both logo and background media files
        // Bind the callback to maintain MST context
        const uploadStatusCallback = (isUploading) => self.setIsUploadingMedia(isUploading);

        if (self.designerViewStore?.logoStore?.logoMediaFile) {
          self.designerViewStore.logoStore.logoMediaFile.setUploadStatusConsumer(uploadStatusCallback);
        }
        if (self.designerViewStore?.logoStore?.backgroundMediaFile) {
          self.designerViewStore.logoStore.backgroundMediaFile.setUploadStatusConsumer(uploadStatusCallback);
        }
      },
      cancelAllMediaUploads() {
        // Cancel any ongoing uploads when modal closes
        if (self.designerViewStore?.logoStore?.logoMediaFile) {
          self.designerViewStore.logoStore.logoMediaFile.cancelMediaFileUpload();
        }
        if (self.designerViewStore?.logoStore?.backgroundMediaFile) {
          self.designerViewStore.logoStore.backgroundMediaFile.cancelMediaFileUpload();
        }
        // Reset the uploading flag
        self.isUploadingMedia = false;
      },
      resetUploadCancelFlags() {
        // Reset cancel flags when modal opens
        if (self.designerViewStore?.logoStore?.logoMediaFile) {
          self.designerViewStore.logoStore.logoMediaFile.resetCancelFlag();
        }
        if (self.designerViewStore?.logoStore?.backgroundMediaFile) {
          self.designerViewStore.logoStore.backgroundMediaFile.resetCancelFlag();
        }
      },
      discardChanges(currentState) {
        self.designerViewStore = currentState.designerModalState.designerViewStore
      }
    });
  }).views((self) => ({}))

